import dayjs from "dayjs"
import { DATE_FORMAT_SHOW } from "src/utils/constants"
import {
    renderCellDate,
    renderTextColumn,
} from "src/utils/table-render-functions"
import { formatStringToFloatDecimalCase } from "src/utils/format-decimal"
import { formatCpfCnpj } from "src/utils/format-cpf-cnpj"
import { formatSinal } from "src/v2/utils/formatter/format-sinal"

const formatDate = (date) => {
    if (!date) return "-"
    return dayjs(date).format(DATE_FORMAT_SHOW)
}

const formatCnpjRaiz = (value) => {
    if (!value) return "-"
    return `RAIZ CNPJ ${value}`
}

const getEmissionHeaderSections = ({
    characterData = {},
    contraparteData = {},
    cetip,
    isin,
}) => [
    {
        fields: [
            {
                label: "CETIP",
                value: characterData?.CETIP ?? cetip ?? "-",
                xs: 6,
                sm: 6,
                md: 3,
            },
            {
                label: "ISIN",
                value: characterData?.ISIN ?? isin ?? "-",
                xs: 6,
                sm: 6,
                md: 3,
            },
            {
                label: "Data Vencimento",
                value: formatDate(characterData?.DataVencimento),
                xs: 6,
                sm: 6,
                md: 3,
            },
            {
                label: "Data Resgate Antecipado",
                value: formatDate(characterData?.DataResgateAntecipado),
                xs: 6,
                sm: 6,
                md: 3,
            },
        ],
    },
    {
        fields: [
            {
                label: "CPF/CNPJ Devedor",
                value: formatCpfCnpj(
                    contraparteData?.CPFCNPJ_Devedor ??
                        characterData?.CPFCNPJ_Devedor
                ),
                xs: 6,
                sm: 6,
                md: 3,
            },
            {
                label: "Devedor",
                value: contraparteData?.Devedor ?? characterData?.Devedor ?? "-",
                xs: 6,
                sm: 6,
                md: 3,
            },
            {
                label: "Setor",
                value: contraparteData?.Setor ?? characterData?.Setor ?? "-",
                xs: 6,
                sm: 6,
                md: 3,
            },
            {
                label: "Subsetor",
                value:
                    contraparteData?.Subsetor ??
                    contraparteData?.SubSetor ??
                    characterData?.Subsetor ??
                    characterData?.SubSetor ??
                    "-",
                xs: 6,
                sm: 6,
                md: 3,
            },
        ],
    },
]

const getSimpleEmissionHeaderSections = ({
    characterData = {},
    cetip,
    isin,
}) => [
    {
        fields: [
            {
                label: "CETIP",
                value: characterData?.CETIP ?? cetip ?? "-",
                xs: 6,
                sm: 6,
                md: 3,
            },
            {
                label: "ISIN",
                value: characterData?.ISIN ?? isin ?? "-",
                xs: 6,
                sm: 6,
                md: 3,
            },
        ],
    },
]

const getDebtorHeaderSections = ({
    characterData = {},
    contraparteData = {},
    cpfCnpj,
}) => [
    {
        fields: [
            {
                label: "Razão Social",
                value:
                    contraparteData?.Devedor ??
                    contraparteData?.RazaoSocial ??
                    "-",
                xs: 6,
                sm: 6,
                md: 3,
            },
            {
                label: "CPF/CNPJ",
                value: formatCpfCnpj(
                    contraparteData?.CPFCNPJ_Devedor ?? cpfCnpj
                ),
                xs: 6,
                sm: 6,
                md: 3,
            },
            
            {
                label: "Setor",
                value: contraparteData?.Setor ?? characterData?.Setor ?? "-",
                xs: 6,
                sm: 6,
                md: 3,
            },
            {
                label: "Subsetor",
                value:
                    contraparteData?.Subsetor ??
                    contraparteData?.SubSetor ??
                    characterData?.Subsetor ??
                    characterData?.SubSetor ??
                    "-",
                xs: 6,
                sm: 6,
                md: 3,
            },
        ],
    },
]

const getEmissionDebtorHeaderSections = ({
    contraparteData = {},
    cpfCnpj,
}) => [
    {
        fields: [
            {
                label: "Razão Social",
                value:
                    contraparteData?.Devedor ??
                    contraparteData?.RazaoSocial ??
                    "-",
                xs: 6,
                sm: 6,
                md: 3,
            },
            {
                label: "CPF/CNPJ",
                value: formatCpfCnpj(
                    contraparteData?.CPFCNPJ_Devedor ?? cpfCnpj
                ),
                xs: 6,
                sm: 6,
                md: 3,
            },{
                label: "Grupo Econômico",
                value: contraparteData?.GrupoEconomico ?? "-",
                optionalText: formatCnpjRaiz(
                    contraparteData?.GrupoEconomico_RaizCNPJ
                ),
                xs: 6,
                sm: 6,
                md: 3,
            },
        ],
    },
]

export const getEmissionSummaryModalConfig = ({
    admin,
    characterData,
    contraparteData,
    cetip,
    isin,
    cpfCnpj,
    stateRating,
    ratingActions,
    stateDebtorEmission,
    debtorEmissionActions,
    lastEventCreditEmissionState,
    lastEventCreditEmissionActions,
    lastEventCreditDebtorState,
    lastEventCreditDebtorActions,
    stateIndexLiquidity,
    indexLiquidityActions,
    lastBusinessState,
    lastBusinessActions,
    statePriceProcess,
    priceProcessActions,
}) => ({
    last_emission: {
        title: "Histórico Rating Emissão",
        loadData: async () => await ratingActions.loadTableRows(),
        sections: [
            ...getEmissionHeaderSections({
                characterData,
                contraparteData,
                cetip,
                isin,
            }),
            {
                type: "edit-table",
                btnAddTitle: "ADICIONAR RATING",
                rowHeight: "auto",
                columns: [
                    {
                        field: "DataReferencia",
                        typeComponent: "date",
                        headerName: "Data Referência",
                        minWidth: 130,
                        editable: admin,
                        renderCell: renderCellDate,
                    },
                    {
                        field: "DataValidade",
                        typeComponent: "date",
                        headerName: "Data Validade",
                        minWidth: 130,
                        editable: admin,
                        renderCell: renderCellDate,
                    },
                    {
                        field: "Fonte",
                        headerName: "Fonte",
                        minWidth: 100,
                        editable: admin,
                    },
                    {
                        field: "ScoreRating",
                        headerName: "Score Rating",
                        minWidth: 100,
                        editable: admin,
                    },
                    {
                        field: "Rating",
                        headerName: "Rating",
                        minWidth: 100,
                        editable: admin,
                    },
                    {
                        field: "Sinal",
                        headerName: "Sinal",
                        minWidth: 100,
                        editable: admin,
                        emptyValue: "",
                        formatter: formatSinal,
                    },
                    {
                        field: "Perspectiva",
                        headerName: "Perspectiva",
                        minWidth: 100,
                        editable: admin,
                    },
                    {
                        field: "Observacao",
                        headerName: "Observação",
                        minWidth: stateRating.minWidthObservacao,
                        editable: admin,
                        renderCell: renderTextColumn,
                    },
                    {
                        field: "AnaliseCreditoEmissao",
                        headerName: "Análise Crédito Emissão",
                        minWidth: stateRating.minWidthAnaliseCredito,
                        editable: admin,
                        renderCell: renderTextColumn,
                    },
                ],
                rows: stateRating.rows,
                loading: stateRating.loading,
                alert: stateRating.alert,
                actions: {
                    addRow: ratingActions.addRow,
                    deleteRow: ratingActions.deleteRow,
                    updateRow: ratingActions.updateRow,
                    closeAlert: ratingActions.closeAlert,
                },
                disableAddRow: !admin,
                disableDeleteRow: !admin,
            },
        ],
    },

    last_debtor: {
        title: "Histórico Rating Devedor",
        loadData: async () => await debtorEmissionActions.loadTableRows(),
        sections: [
            ...getDebtorHeaderSections({
                contraparteData,
                cpfCnpj,
            }),
            {
                type: "edit-table",
                btnAddTitle: "ADICIONAR RATING",
                rowHeight: "auto",
                columns: [
                    {
                        field: "DataReferencia",
                        minWidth: 130,
                        headerName: "Data Referência",
                        flex: 1,
                        editable: admin,
                        renderCell: renderCellDate,
                    },
                    {
                        field: "DataValidade",
                        minWidth: 130,
                        headerName: "Data Validade",
                        flex: 1,
                        editable: admin,
                        renderCell: renderCellDate,
                    },
                    {
                        field: "Fonte",
                        minWidth: 100,
                        headerName: "Fonte",
                        flex: 1,
                        editable: admin,
                    },
                    {
                        field: "ScoreRating",
                        minWidth: 100,
                        headerName: "Score Rating",
                        flex: 1,
                        editable: admin,
                    },
                    {
                        field: "Rating",
                        minWidth: 100,
                        headerName: "Rating",
                        flex: 1,
                        editable: admin,
                    },
                    {
                        field: "Sinal",
                        minWidth: 100,
                        headerName: "Sinal",
                        flex: 1,
                        editable: admin,
                        emptyValue: "",
                        formatter: formatSinal,
                    },
                    {
                        field: "Perspectiva",
                        minWidth: 100,
                        headerName: "Perspectiva",
                        flex: 1,
                        editable: admin,
                    },
                    {
                        field: "Observacao",
                        headerName: "Observação",
                        flex: 1,
                        minWidth: stateRating.minWidthObservacao,
                        editable: admin,
                        renderCell: renderTextColumn,
                    },
                    {
                        field: "AnaliseCreditoDevedor",
                        headerName: "Análise Crédito Devedor",
                        flex: 1,
                        minWidth: stateRating.minWidthAnaliseCredito,
                        editable: admin,
                        renderCell: renderTextColumn,
                    },
                ],
                rows: stateDebtorEmission.rows,
                loading: stateDebtorEmission.loading,
                alert: stateDebtorEmission.alert,
                actions: {
                    addRow: debtorEmissionActions.addRow,
                    deleteRow: debtorEmissionActions.deleteRow,
                    updateRow: debtorEmissionActions.updateRow,
                    closeAlert: debtorEmissionActions.closeAlert,
                },
                disableAddRow: !admin,
                disableDeleteRow: !admin,
            },
        ],
    },

    history_event_credit_emission: {
        title: "Histórico Evento de Crédito Emissão",
        loadData: async () =>
            await lastEventCreditEmissionActions.loadTableRows(),
        sections: [
            ...getSimpleEmissionHeaderSections({
                characterData,
                cetip,
                isin,
            }),
            {
                type: "edit-table",
                btnAddTitle: "ADICIONAR EVENTO",
                rowHeight: "auto",
                columns: [
                    {
                        field: "DataReferencia",
                        headerName: "Data Referência",
                        minWidth: 150,
                        flex: 1,
                        editable: admin,
                        renderCell: renderCellDate,
                    },
                    {
                        field: "EventoCreditoEmissao",
                        headerName: "Evento",
                        flex: 1,
                        minWidth: "min-content",
                        editable: admin,
                        renderCell: renderTextColumn,
                    },
                    {
                        field: "ScoreEvento",
                        headerName: "Score Evento",
                        editable: admin,
                        flex: 1,
                        minWidth: 120,
                    },
                ],
                rows: lastEventCreditEmissionState.rows,
                loading: lastEventCreditEmissionState.loading,
                alert: lastEventCreditEmissionState.alert,
                actions: {
                    addRow: lastEventCreditEmissionActions.addRow,
                    deleteRow: lastEventCreditEmissionActions.deleteRow,
                    updateRow: lastEventCreditEmissionActions.updateRow,
                    closeAlert: lastEventCreditEmissionActions.closeAlert,
                },
                disableAddRow: !admin,
                disableDeleteRow: !admin,
            },
        ],
    },

    history_event_credit_debtor: {
        title: "Histórico Evento de Crédito Devedor",
        loadData: async () =>
            await lastEventCreditDebtorActions.loadTableRows(),
        sections: [
            ...getEmissionDebtorHeaderSections({
                contraparteData,
                cpfCnpj,
            }),
            {
                type: "edit-table",
                btnAddTitle: "ADICIONAR EVENTO",
                rowHeight: "auto",
                columns: [
                    {
                        field: "DataReferencia",
                        headerName: "Data Referência",
                        flex: 1,
                        editable: admin,
                        renderCell: renderCellDate,
                    },
                    {
                        field: "EventoCreditoDevedor",
                        headerName: "Evento",
                        flex: 1,
                        minWidth: "min-content",
                        editable: admin,
                        renderCell: renderTextColumn,
                    },
                    {
                        field: "ScoreEvento",
                        headerName: "Score Evento",
                        flex: 1,
                        editable: admin,
                    },
                ],
                rows: lastEventCreditDebtorState.rows,
                loading: lastEventCreditDebtorState.loading,
                alert: lastEventCreditDebtorState.alert,
                actions: {
                    addRow: lastEventCreditDebtorActions.addRow,
                    deleteRow: lastEventCreditDebtorActions.deleteRow,
                    updateRow: lastEventCreditDebtorActions.updateRow,
                    closeAlert: lastEventCreditDebtorActions.closeAlert,
                },
                disableAddRow: !admin,
                disableDeleteRow: !admin,
            },
        ],
    },

    index_liquidity: {
        title: "Histórico Índice Liquidez",
        loadData: async () => await indexLiquidityActions.loadTableRows(),
        sections: [
            ...getSimpleEmissionHeaderSections({
                characterData,
                cetip,
                isin,
            }),
            {
                type: "edit-table",
                btnAddTitle: "ADICIONAR ÍNDICE LIQUIDEZ",
                rowHeight: undefined,
                columns: [
                    {
                        field: "DataReferencia",
                        headerName: "Data Referência",
                        flex: 1,
                        renderCell: renderCellDate,
                        editable: admin,
                    },
                    {
                        field: "IndiceLiquidez",
                        headerName: "Índice Liquidez",
                        flex: 1,
                        editable: admin,
                    },
                ],
                rows: stateIndexLiquidity.rows,
                loading: stateIndexLiquidity.loading,
                alert: stateIndexLiquidity.alert,
                actions: {
                    addRow: indexLiquidityActions.addRow,
                    deleteRow: indexLiquidityActions.deleteRow,
                    updateRow: indexLiquidityActions.updateRow,
                    closeAlert: indexLiquidityActions.closeAlert,
                },
                disableAddRow: !admin,
                disableDeleteRow: !admin,
            },
        ],
    },

    history_last_negocitacions: {
        title: "Histórico Das Últimas Negociações",
        loadData: async () => await lastBusinessActions.loadTableRows(),
        sections: [
            ...getSimpleEmissionHeaderSections({
                characterData,
                cetip,
                isin,
            }),
            {
                type: "table",
                title: "Últimas Negociações",
                columns: [
                    {
                        label: "Data Referência",
                        key: "DataReferencia",
                        flex: 1,
                        minWidth: 160,
                        formatter: (value) =>
                            value ? dayjs(value).format(DATE_FORMAT_SHOW) : "-",
                    },
                    {
                        label: "PU MTM",
                        key: "PU_MTM",
                        flex: 1,
                        minWidth: 140,
                        formatter: (value) =>
                            formatStringToFloatDecimalCase(value, 4, 4),
                    },
                    {
                        label: "%PUPAR",
                        key: "PercentualPUPAR",
                        flex: 1,
                        minWidth: 140,
                        formatter: (value) =>
                            formatStringToFloatDecimalCase(value, 2, 2),
                    },
                    {
                        label: "Taxa MTM",
                        key: "TaxaMTM",
                        flex: 1,
                        minWidth: 140,
                        formatter: (value) =>
                            formatStringToFloatDecimalCase(value, 4, 4),
                    },
                    {
                        label: "Spread Crédito",
                        key: "SpreadCreditoFinal",
                        flex: 1,
                        minWidth: 160,
                        formatter: (value) =>
                            formatStringToFloatDecimalCase(value, 4, 4),
                    },
                    {
                        label: "Volume Financeiro",
                        key: "VolumeFinanceiro",
                        flex: 1,
                        minWidth: 170,
                        formatter: (value) =>
                            formatStringToFloatDecimalCase(value, 2, 2),
                    },
                ],
                rows: lastBusinessState.rows,
            },
        ],
    },

    tax_indicative: {
        title: "Histórico Taxa Indicativa",
        loadData: async () => await priceProcessActions.loadTableRows(),
        sections: [
            ...getSimpleEmissionHeaderSections({
                characterData,
                cetip,
                isin,
            }),
            {
                type: "edit-table",
                btnAddTitle: "ADICIONAR PREÇOS PROCESSADOS",
                rowHeight: undefined,
                columns: [
                    {
                        key: "DataReferencia",
                        label: "Data Referência",
                        flex: 1,
                        renderCell: renderCellDate,
                        editable: admin,
                    },
                    {
                        field: "TaxaMTM",
                        headerName: "Taxa MTM",
                        flex: 1,
                        editable: admin,
                        renderCell: ({ value }) =>
                            formatStringToFloatDecimalCase(value, 3, 3),
                    },
                    {
                        field: "PercentualPUPAR",
                        headerName: "Percentual PUPAR",
                        flex: 1,
                        editable: admin,
                        renderCell: ({ value }) =>
                            formatStringToFloatDecimalCase(value, 3, 3),
                    },
                    {
                        field: "SpreadCredito",
                        headerName: "Spread Crédito",
                        flex: 1,
                        editable: admin,
                        renderCell: ({ value }) =>
                            formatStringToFloatDecimalCase(value, 3, 3),
                    },
                ],
                rows: statePriceProcess.rows,
                loading: statePriceProcess.loading,
                alert: statePriceProcess.alert,
                actions: {
                    addRow: priceProcessActions.addRow,
                    deleteRow: priceProcessActions.deleteRow,
                    updateRow: priceProcessActions.updateRow,
                    closeAlert: priceProcessActions.closeAlert,
                },
                disableAddRow: !admin,
                disableDeleteRow: !admin,
            },
        ],
    },
})